import { Injectable } from '@angular/core';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import { Highway } from '../models/highway';
import { Chuva } from '../models/chuva';
import { Tempo } from '../models/tempo';
import { Localidade } from '../models/localidade';
import { Observable } from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import { throwToolbarMixedModesError } from '@angular/material/toolbar';
import { GeoJSON, FeatureCollection, Point, MultiPolygon } from 'geojson';
import { AnonymousSubject } from 'rxjs/internal/Subject';
import { Alerta } from '../models/alerta';
import { environment } from 'src/environments/environment';
import { Session } from '../models/session';
import { Iteration } from '../models/iteration';
import { RainPatternsResponse } from '../models/rain-patterns-response';
import { RainDataByIntensities } from '../models/rain-data-by-intensities';

@Injectable({
  providedIn: 'root'
})
export class ServiceService {

  private urlHighway = `${environment.serverBaseUrl}/highway`;
  private urlTempo = `${environment.serverBaseUrl}/tempo`;
  private urlChuva = `${environment.serverBaseUrl}/chuva`;
  private urlTempoPadrao = `${environment.serverBaseUrl}/chuva/padroes`
  private urlChuvaIntensidade = `${environment.serverBaseUrl}/chuva/intensidades`
  private urlEvento = `${environment.serverBaseUrl}/evento`;
  private urlRio = `${environment.serverBaseUrl}/rio`;
  private urlAlerta = `${environment.serverBaseUrl}/alerta`;
  private urlBairro = `${environment.serverBaseUrl}/bairros`;
  private urlSessions = `${environment.serverBaseUrl}/route/sessions`;
  private urlIterations = `${environment.serverBaseUrl}/route/iterations`;
  private urlIterationGeometries = `${environment.serverBaseUrl}/route/geometries`;
  private urlVoronoiPolygons =  `${environment.serverBaseUrl}/voronoi`;
  private urlCameras = `${environment.serverBaseUrl}/camera`;

  /** URL da rota de classificações de alagamento pelas câmeras de segurança. */
  private urlRainClassesFromCameras = `${environment.serverBaseUrl}/camera/classificacoes`;

  /** URL da rota do calendário de chuva para um ano. */
  private urlMax24HourAggregatePerDay = `${environment.serverBaseUrl}/chuva/maximoDia`;

   /** URL da rota do calendário de ocorrencia para um ano. */
  private urlEventPerDay = `${environment.serverBaseUrl}/evento/porDia`;
  
  constructor(protected http: HttpClient) { 

  }


  getAll(): Observable<GeoJSON.FeatureCollection>{    //observa assincronamente

    return this.http.get<GeoJSON.FeatureCollection>(this.urlHighway,{responseType: "json"}).pipe(
      tap((_) => console.log("Got all HighwayData")));
    
  }

  /**
   * Busca os dados dos rios.
   * 
   * @returns observable com os dados dos rios.
   */
  getAllRivers(): Observable<FeatureCollection> {

    return this.http.get<FeatureCollection>(this.urlRio).pipe(
      tap(
        (_) => console.log("Got all River Data")
      )
    );
  }

  /**
   * Busca os polígonos dos bairros.
   * @returns Observable da busca dos bairros.
   */
  getAllNeighborhoods() {
    return this.http.get<FeatureCollection<MultiPolygon>>(this.urlBairro).pipe(
      tap(_ => console.log('Got all Neighborhood Data'))
    );
  }

  /**
   * Busca os polígonos de voronoi.
   * @returns Observable da busca das células de voronoi.
   */
  getVoronoiPolygons(): Observable<FeatureCollection> {
    return this.http.get<FeatureCollection>(this.urlVoronoiPolygons).pipe(
      tap(_ => console.log('Got all Voronoi Polygons Data'))
    );
  }

  /**
   * 
   * 
   * @param dateTime
   * @returns 
   */
  getTimeByHour(dateTime: string): Observable<any>{
    return this.http.get<Tempo[]>(this.urlTempo,{ params: { dateTime } }).pipe(
      tap((_) => console.log("Got all Time Data")));
  }

  getSessionList(dia: number, mes: number, ano: number): Observable<any>{
    return this.http.get<Session[]>(this.urlSessions,{params:{dia: dia, mes: mes, ano: ano}}).pipe(
      tap((_) => console.log("Got list of sessions")));
  }

  getIterationList(session_id: number): Observable<any>{
    return this.http.get<Session[]>(this.urlIterations,{params:{session_id:session_id}}).pipe(
      tap((_) => console.log("Got list of iterations")));
  }
  
  /**
   * Busca as geometrias das rotas de atendimento das ocorrẽncias.
   * Chamamos de iteração uma execução da heurística de cálculo da rota. 
   * 
   * @param iterationIds ids das iterações a serem buscadas.
   * @returns 
   */
  getIterationGeometries(iterationIds: number[]): Observable<any> {
    
    return this.http.get<Iteration[]>(this.urlIterationGeometries,
        {
          params: {
            iterationIds: iterationIds
          }
        }
      ).pipe(
      tap((_) => console.log("Got geometries associated with selected iteration")));
  }

  /** */
  getRainDataByTimeId(time_id: number): Observable<GeoJSON.FeatureCollection>{
    return this.http.get<GeoJSON.FeatureCollection>(this.urlChuva,{params:{time_id: time_id}}).pipe(
      tap((_) => console.log("Got all Rain Data")));
  }

  /**
   * Busca estações e tempos que apresentam um dado padrão de intensidades dos acumulados
   * de chuvas antes do tempo fornecido. As linhas são ordenadas pela quantidade de
   * ocorrências registradas até 24 horas antes do tempo do registro da chuva.
   * 
   * @param time_id id do tempo.
   * @param id_estacao id da estação.
   * @param intensities intensidades dos acumulados de chuva que a estação apresentou no tempo.
   * @returns 
   */
  getTimeByPattern(time_id: string, id_estacao:string, 
    intensities: {[key: string]: string}): Observable<RainPatternsResponse> {
    
    return this.http.post<{tempos: any, ocorrencias: any}>(
        this.urlTempoPadrao,
        {
          time_id,
          id_estacao,
          intensities,
        }
      )
      .pipe(tap((_) => console.log("Got all time data by patterns")));
  }

  /**
   * Busca as linhas do tempo da mudança das intensidades de chuva 
   * registradas por cada estação dentro do intervalo de tempo definido.
   * 
   * @param time_id_initial 
   * @param time_id_final 
   * @param fraca 
   * @param moderada 
   * @param forte 
   * @param muitoforte 
   * @returns 
   */
  getRainDataByIntensities(time_id_initial:string,time_id_final:string,fraca:string,moderada:string,forte:string,muitoforte:string): Observable<any[]>{
    
    return this.http.get<RainDataByIntensities[]>(
      this.urlChuvaIntensidade,
      {
        params: {
          time_id_initial,
          time_id_final,
          fraca,
          moderada,
          forte,
          muitoforte
        }
      })
      .pipe(tap((_) => console.log("Got all time data by intensities")));
  }

  /**
   * Busca os dados sobre os eventos que ocorreram em um tempo específico.
   * 
   * @param time_id id do tempo selecionado pelo usuário.
   * @returns observable com os dados dos eventos. 
   */
  getEventDataByTimeId(startTimeId: number,endTimeId: number): Observable<FeatureCollection<Point>> {
    return this.http.get<FeatureCollection<Point>>(this.urlEvento, { params: { startTimeId, endTimeId} })
      .pipe(
        tap(_ => console.log("Got all Event Data"))
      );
  }

  /**
   * Busca alertas e avisos cujos intervalos de tempo abrangem o horário passado como parâmetro. 
   * 
   * @param minuto minuto selecionado
   * @param hora hora selecionada
   * @param dia dia selecionado
   * @param mes mês selecionado
   * @param ano ano selecionado
   */
  getAlertsByTime(minuto: number, hora: number, dia: number, mes: number, ano: number) : Observable<Alerta[]> {

    return this.http.get<Alerta[]>(this.urlAlerta, { 
        params: { minuto, hora, dia, mes, ano }
      })
      .pipe(
        tap(_ => console.log("Got all Alert Data"))
      );
  }

  /**
   * Busca alertas e avisos cujos intervalos de tempo abrangem o id_tempo passado como parâmetro. 
   * 
   * @param timeId 
   */
  getAlertsByTimeId(timeId: number,tooFar:boolean,RT = false) : Observable<Alerta[]> {

    return this.http.get<Alerta[]>(this.urlAlerta, { 
      params: { timeId,RT,tooFar }
    })
    .pipe(
      tap(_ => console.log("Got all Alert Data"))
    );
  }

  /**
   * Obtém um array de registros do maior acumulado de 24 horas no fim do dia, 
   * por dia, dentro de um ano. Chamamos isso de calendário de analise.
   * @param year ano do calendário de chuva.
   */
  getMax24HourAggregatePerDay(startDate: string, endDate: string, aggregateCol: string) {

    return this.http.get<any[]>(this.urlMax24HourAggregatePerDay, {
      params: { inicio: startDate, fim: endDate, acumulado: aggregateCol }
    });
  }

  /**
   * Obtém um array de registros das ocorrencias do dia dentro de um ano. Chamamos isso de calendário de analise.
   * @param year ano do calendário de chuva.
   */
  getEventPerDay(startDate: string, endDate: string) {

    return this.http.get<any[]>(this.urlEventPerDay, {
      params: { inicio: startDate, fim: endDate }
    });
  }

  /**
   * Busca os registros de câmeras de trânsito.
   * @returns 
   */
  getCameras(): Observable<FeatureCollection<Point>> {
    return this.http.get<FeatureCollection<Point>>(this.urlCameras);
  }

  /**
   * Busca classificações da chuva / alagamento a partir das câmeras em um intervalo
   * de tempo específico.
   * @param startDatetime 
   * @param endDatetime 
   * @returns 
   */
  getRainClassesFromCameras(datetime: string): Observable<any> {
    return this.http.get(this.urlRainClassesFromCameras, {
      params: { tempo: datetime, }
    });
  }

  /**
   * Busca os nomes das estações de uma dada fonte de dados.
   * @param source fonte de dados, p. ex., PREFEITURA NITERÓI.
   */
  getStationsData(source: string) {
    return this.http.get<any[]>(`${environment.serverBaseUrl}/chuva/dadosEstacoes`, { params: { fonte: source } });
  }
} 
